import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";

const ThreadList = ({ threads, activeId }) => (
  <div className="hb-card overflow-hidden" data-testid="message-thread-list">
    {threads === null && <p className="p-6 text-sm text-[var(--hb-muted)]">Loading...</p>}
    {threads?.length === 0 && (
      <p className="p-6 text-sm text-[var(--hb-muted)]" data-testid="messages-empty">
        No messages yet. Open a member's profile and tap Message to start a conversation.
      </p>
    )}
    {threads?.map((t) => (
      <Link key={t.id} to={`/messages/${t.member.id}`} data-testid={`thread-${t.member.id}`}
        className={`flex items-center gap-3 px-4 py-3 border-b border-[var(--hb-line)] last:border-0 transition-colors ${
          t.member.id === activeId ? "bg-[var(--hb-lime-soft)]" : "hover:bg-[#F5F5F0]"}`}>
        <UserAvatar user={t.member} size={42} />
        <div className="flex-1 min-w-0">
          <div className="flex justify-between gap-2">
            <span className={`truncate ${t.unread ? "font-semibold" : ""}`}>{t.member.name}</span>
            <span className="text-[11px] text-[var(--hb-muted)] shrink-0">{timeAgo(t.last_at)}</span>
          </div>
          <div className="text-sm text-[var(--hb-muted)] truncate">{t.last_body}</div>
        </div>
        {t.unread > 0 && <span className="rounded-full bg-[var(--hb-ink)] text-[var(--hb-lime)] text-[10px] font-bold px-1.5" data-testid={`thread-unread-${t.member.id}`}>{t.unread}</span>}
      </Link>
    ))}
  </div>
);

const Conversation = ({ memberId, onSent, onRead }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    let first = true;
    const load = () => api.get(`/messages/${memberId}`).then((r) => {
      setData(r.data);
      if (first) {
        first = false;
        onRead();
      }
    }).catch(() => navigate("/messages"));
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [memberId, navigate]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [data?.messages.length]);

  const send = async (e) => {
    e.preventDefault();
    try {
      const { data: msg } = await api.post(`/messages/${memberId}`, { body: text });
      setData({ ...data, messages: [...data.messages, msg] });
      setText("");
      onSent();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!data) return <div className="hb-card p-6 text-[var(--hb-muted)]">Loading conversation...</div>;
  return (
    <div className="hb-card flex flex-col h-[calc(100vh-260px)] md:h-[calc(100vh-220px)] min-h-[380px]" data-testid="conversation">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--hb-line)]">
        <Link to="/messages" className="md:hidden p-1" data-testid="conversation-back"><ArrowLeft size={18} /></Link>
        <Link to={`/members/${data.member.id}`} className="flex items-center gap-3" data-testid="conversation-member-link">
          <UserAvatar user={data.member} size={38} />
          <div>
            <div className="font-semibold leading-tight">{data.member.name}</div>
            {data.member.business && <div className="text-xs text-[var(--hb-muted)]">{data.member.business}</div>}
          </div>
        </Link>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="conversation-messages">
        {data.messages.length === 0 && <p className="text-sm text-center text-[var(--hb-muted)] py-10">Say hi to {data.member.name.split(" ")[0]}!</p>}
        {data.messages.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
              <div className={`max-w-[78%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${
                mine ? "bg-[var(--hb-lime)] text-[var(--hb-ink)] rounded-br-md" : "bg-[#F0F0EA] rounded-bl-md"}`}>
                {m.body}
                <div className="text-[10px] opacity-60 mt-1 text-right">{timeAgo(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 p-3 border-t border-[var(--hb-line)]">
        <input className="hb-input" placeholder="Write a message..." value={text} onChange={(e) => setText(e.target.value)} data-testid="message-input" />
        <button className="btn-primary !px-4" disabled={!text.trim()} data-testid="message-send"><Send size={15} /></button>
      </form>
    </div>
  );
};

export default function MessagesPage() {
  const { memberId } = useParams();
  const { refreshUnread } = useOutletContext();
  const [threads, setThreads] = useState(null);
  const load = () => api.get("/messages/threads").then((r) => setThreads(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, [memberId]);

  return (
    <div data-testid="messages-page">
      <div className={memberId ? "hidden md:block" : ""}>
        <div className="label-accent">Boo to boo</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-1 mb-6">Messages</h1>
      </div>
      <div className="grid md:grid-cols-[320px_1fr] gap-6">
        <div className={memberId ? "hidden md:block" : ""}><ThreadList threads={threads} activeId={memberId} /></div>
        {memberId ? (
          <Conversation key={memberId} memberId={memberId} onSent={load} onRead={() => { load(); refreshUnread(); }} />
        ) : (
          <div className="hidden md:grid hb-card place-items-center text-[var(--hb-muted)] min-h-[380px]">Pick a conversation</div>
        )}
      </div>
    </div>
  );
}
